import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { forumService } from '../../services/forumService';
import { supabase } from '../../services/supabaseClient';
import type { ForumCategory, ForumTopic } from '../../types/forum';
import PageLoader from '../../components/common/PageLoader';
import '../../styles/pages/ForumsPage.css';

const ForumsHomePage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();

  const [categories, setCategories] = useState<(ForumCategory & { latestTopic?: ForumTopic })[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentDiscussions, setRecentDiscussions] = useState<Array<{
    id: string;
    title: string;
    created_at: string;
    post_count: number;
    author: string;
    author_avatar_url?: string;
    category_slug: string;
    category_title: string;
  }>>([]);


  // Real-time subscription refs
  const topicsChannelRef = useRef<any>(null);
  const postsChannelRef = useRef<any>(null);
  const authSubscriptionRef = useRef<any>(null);

  useEffect(() => {
    loadForumData();
  }, []);

  // Set up real-time subscriptions for forum updates
  useEffect(() => {
    console.log('🔄 Setting up real-time subscriptions for forum home page');

    // Debounce function to prevent too many rapid updates
    let updateTimeout: NodeJS.Timeout;
    const debouncedUpdate = (updateFunction: () => Promise<void>) => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(updateFunction, 1000); // 1 second debounce
    };

    // Function to set up realtime subscriptions
    const setupRealtimeSubscriptions = () => {
      // Clean up existing subscriptions first
      if (topicsChannelRef.current) {
        supabase.removeChannel(topicsChannelRef.current);
      }
      if (postsChannelRef.current) {
        supabase.removeChannel(postsChannelRef.current);
      }

      // Subscribe to new topics
      topicsChannelRef.current = supabase
        .channel('forum_topics_home')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'forum_topics'
          },
          async (payload) => {
            console.log('🔔 Real-time: New topic received:', payload);

            // Debounced refresh of recent discussions
            debouncedUpdate(async () => {
              try {
                const recentDiscussionsData = await forumService.getRecentDiscussions(10);
                setRecentDiscussions(recentDiscussionsData);
                console.log('✅ Recent discussions updated with new topic');
              } catch (error) {
                console.error('❌ Error updating recent discussions:', error);
              }
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'forum_topics'
          },
          async (payload) => {
            console.log('🔄 Real-time: Topic updated:', payload);

            // Debounced refresh of recent discussions
            debouncedUpdate(async () => {
              try {
                const recentDiscussionsData = await forumService.getRecentDiscussions(10);
                setRecentDiscussions(recentDiscussionsData);
                console.log('✅ Recent discussions updated with topic changes');
              } catch (error) {
                console.error('❌ Error updating recent discussions:', error);
              }
            });
          }
        )
        .subscribe((status, err) => {
          console.log('📡 Topics subscription status:', status);
          if (err) {
            console.error('❌ Topics subscription error:', err);
          }
        });

      // Subscribe to new posts (affects post counts and user rankings)
      postsChannelRef.current = supabase
        .channel('forum_posts_home')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'forum_posts'
          },
          async (payload) => {
            console.log('🔔 Real-time: New post received:', payload);

            // Debounced refresh of recent discussions
            debouncedUpdate(async () => {
              try {
                const recentDiscussionsData = await forumService.getRecentDiscussions(10);
                setRecentDiscussions(recentDiscussionsData);
                console.log('✅ Recent discussions updated with new post');
              } catch (error) {
                console.error('❌ Error updating forum data:', error);
              }
            });
          }
        )
        .subscribe((status, err) => {
          console.log('📡 Posts subscription status:', status);
          if (err) {
            console.error('❌ Posts subscription error:', err);
          }
        });
    };

    // Initial setup of subscriptions
    setupRealtimeSubscriptions();

    // Listen for auth state changes (including token refresh)
    authSubscriptionRef.current = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state change in ForumsHomePage:', event);

      if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token refreshed, resubscribing to realtime channels...');
        setupRealtimeSubscriptions();
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out, cleaning up subscriptions');
        if (topicsChannelRef.current) {
          supabase.removeChannel(topicsChannelRef.current);
        }
        if (postsChannelRef.current) {
          supabase.removeChannel(postsChannelRef.current);
        }
      }
    });

    // Cleanup subscriptions on unmount
    return () => {
      clearTimeout(updateTimeout);
      if (topicsChannelRef.current) {
        console.log('🧹 Cleaning up topics subscription');
        supabase.removeChannel(topicsChannelRef.current);
      }
      if (postsChannelRef.current) {
        console.log('🧹 Cleaning up posts subscription');
        supabase.removeChannel(postsChannelRef.current);
      }
      if (authSubscriptionRef.current) {
        console.log('🧹 Cleaning up auth subscription');
        authSubscriptionRef.current.subscription?.unsubscribe();
      }
    };
  }, []);

  const loadForumData = async () => {
    try {
      // Step 1: Fetch categories first (needed for categoryIds)
      const categoriesData = await forumService.getCategories();
      const categoryIds = categoriesData.map(c => c.id);

      // Step 2: OPTIMIZATION - Fetch recent discussions AND latest topics in parallel
      // This reduces total load time by running independent queries concurrently
      const parallelPromises: Promise<any>[] = [
        forumService.getRecentDiscussions(10)
      ];

      // Only fetch latest topics if we have categories
      if (categoryIds.length > 0) {
        parallelPromises.push(
          (async () => {
            return await supabase
              .from('forum_topics')
              .select(`
                *,
                category:forum_categories!forum_topics_category_id_fkey(title, slug),
                created_by_user:users!forum_topics_created_by_fkey(username, display_name, avatar_url)
              `)
              .in('category_id', categoryIds)
              .order('last_activity_at', { ascending: false });
          })()
        );
      }

      const [recentDiscussionsResult, latestTopicsResult] = await Promise.allSettled(parallelPromises);

      // Handle recent discussions data
      if (recentDiscussionsResult.status === 'fulfilled') {
        setRecentDiscussions(recentDiscussionsResult.value);
      } else {
        console.error('Error loading recent discussions:', recentDiscussionsResult.reason);
      }

      // Handle latest topics data
      let latestTopicsByCategory: Record<string, any> = {};

      if (latestTopicsResult && latestTopicsResult.status === 'fulfilled') {
        const { data: allLatestTopics } = latestTopicsResult.value;

        if (allLatestTopics) {
          const groupedByCategory: Record<string, any[]> = {};

          allLatestTopics.forEach((topic: any) => {
            if (!groupedByCategory[topic.category_id]) {
              groupedByCategory[topic.category_id] = [];
            }
            groupedByCategory[topic.category_id].push(topic);
          });

          // Get the first (latest) topic for each category
          Object.keys(groupedByCategory).forEach(categoryId => {
            latestTopicsByCategory[categoryId] = groupedByCategory[categoryId][0];
          });
        }
      } else if (latestTopicsResult && latestTopicsResult.status === 'rejected') {
        console.error('Error fetching latest topics:', latestTopicsResult.reason);
      }

      // Map latest topics to categories
      const categoriesWithLatestTopics = categoriesData.map(category => ({
        ...category,
        latestTopic: latestTopicsByCategory[category.id]
      }));

      setCategories(categoriesWithLatestTopics);

    } catch (error) {
      console.error('Error loading forum data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date in a more readable format (like "Yesterday 22:34")
  const formatDate = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;

    if (diffDays === 0) {
      return `${t('forums.time.today')} ${timeStr}`;
    } else if (diffDays === 1) {
      return `${t('forums.time.yesterday')} ${timeStr}`;
    } else if (diffDays < 7) {
      const days = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];
      return `${days[date.getDay()]} ${timeStr}`;
    } else {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day} ${timeStr}`;
    }
  };

  if (loading) {
    return (
      <BasePageLayout>
        <PageLoader fullPage text={t('forums.common.loading')} />
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout maxWidth="none" background="white">
      <div className="forums-container">
        {/* Forum Header */}
        <div className="forum-header">
          <div className="header-content">
            <h1 className="forum-title">
              <i className="bi bi-chat-square-dots-fill"></i> {t('forums.common.title')}
            </h1>
            <p className="forum-subtitle">
              {t('forums.common.subtitle')}
            </p>
          </div>
        </div>

        <div className="forum-layout">
          {/* Main Content */}
          <div className="forum-main">
            {/* Categories Section */}
            <div className="categories-section">
              <div className="categories-compact-grid">
                {categories.map((category) => {
                  return (
                    <Link
                      key={category.id}
                      to={`/forums/category/${category.slug}`}
                      className="category-compact-item"
                    >
                      <div
                        className="category-compact-icon"
                        style={{ backgroundColor: `${category.color}15`, color: category.color }}
                      >
                        <span className="category-icon">{category.icon}</span>
                      </div>
                      <div className="category-compact-info">
                        <h3 className="category-compact-title">
                          {t(`forums.categories.titles.${category.slug}`, { defaultValue: category.title })}
                        </h3>
                        <div className="category-compact-stats">
                          <span className="compact-stat">
                            {category.topic_count} {t('forums.common.topics')}
                          </span>
                          <span className="compact-separator">•</span>
                          <span className="compact-stat">
                            {category.post_count} {t('forums.common.posts')}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar - Sticky */}
          <div className="forum-sidebar sticky">
            {/* Recent Discussions */}
            <div className="sidebar-section">
              <h3 className="sidebar-title">
                <i className="bi bi-lightning-charge-fill"></i> {t('forums.common.recentDiscussions')}
              </h3>
              <div className="discussion-list">
                {recentDiscussions.map((discussion) => (
                  <div key={discussion.id} className="discussion-item">
                    <div className="discussion-details">
                      <Link
                        to={`/forums/topic/${discussion.id}`}
                        className="discussion-title"
                      >
                        {discussion.title}
                      </Link>
                      <div className="discussion-meta">
                        <span>
                          in{' '}
                          <Link
                            to={`/forums/category/${discussion.category_slug}`}
                            className="discussion-category"
                          >
                            {t(`forums.categories.titles.${discussion.category_slug}`, { defaultValue: discussion.category_title })}
                          </Link>
                        </span>
                        <span>&bull;</span>
                        <span>{formatDate(discussion.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default ForumsHomePage;
